import React, { useState, useEffect, useCallback } from "react";
import { Button, Select } from "flowbite-react";

import { TeamGoalsModal } from "../../../components/TeamGoalsModal/TeamGoalsModal";
import { useAuth, useToast } from "../../../hooks";
import { NOTIFICATION_TYPES } from "../../../utils";

// PAN-818: the "Medium-signal promotion floor" input is gone. The priority list now shows every
// account with an active risk signal, so the floor no longer changes anything; the page neither
// shows nor sends it (PATCH leaves the stored value alone until the backend field is removed).
export const TeamSettings = () => {
  const [defaultCurrency, setDefaultCurrency] = useState("USD");
  const [csmViewMode, setCsmViewMode] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  // Retained deliberately, unlike the equivalent state on DashboardV2Page: this one is
  // still read, to gate the Save button until the row has loaded. It must NOT be spread
  // into the save payload again — that is the PAN-577 defect.
  const [teamSettings, setTeamSettings] = useState(null);
  const [goalsModalOpen, setGoalsModalOpen] = useState(false);

  const showToast = useToast();
  const { apiClient, teamId, team } = useAuth();

  // PAN-587: the modal fetches its own team-wide summary + nrrProgress on open.
  const handleOpenGoalsModal = useCallback(() => {
    setGoalsModalOpen(true);
  }, []);

  const currencies = [
    { value: "USD", label: "USD - US Dollar", symbol: "$" },
    { value: "EUR", label: "EUR - Euro", symbol: "€" },
  ];

  const loadTeamSettings = async () => {
    setIsLoading(true);
    try {
      // Check localStorage first for cached setting
      const cachedCurrency = localStorage.getItem("defaultCurrency");
      if (cachedCurrency) {
        setDefaultCurrency(cachedCurrency);
      }

      // Fetch team settings from backend
      const response = await apiClient.get(`/team-settings/${teamId}/`);
      setTeamSettings(response.data);
      setDefaultCurrency(response.data.currency || "USD");
      setCsmViewMode(response.data.csm_view_mode || "open");

      // Update localStorage with backend data
      localStorage.setItem("defaultCurrency", response.data.currency || "USD");
    } catch (error) {
      console.error("Error loading team settings:", error);
      showToast({
        content: "Failed to load settings",
        type: NOTIFICATION_TYPES.error,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Load current settings on component mount
  useEffect(() => {
    if (teamId) {
      loadTeamSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId]);

  const handleSave = async () => {
    setIsSaving(true);

    try {
      // Save to localStorage for immediate persistence
      localStorage.setItem("defaultCurrency", defaultCurrency);

      // Send ONLY the fields this page owns. Spreading `...teamSettings` echoed the
      // whole row back, which did two things: it wrote a fabricated `time_zone: "UTC"`
      // over the NULL that HubSpot seeding deliberately leaves (permanent, because the
      // seed guard is `hs_id in {"", "default"}` and never re-runs once hs_id is real),
      // and it reverted any field changed by a sync or by admin between load and save.
      // PATCH, not PUT: `TeamSettingsSerializer` is `fields = "__all__"` with a required
      // `time_zone`, so a narrowed PUT 400s. PAN-577.
      const payload = {
        currency: defaultCurrency,
        // Gated on `is_admin`, matching the control's own gate at the CSM View Mode
        // section below. A non-admin never sees that control, so sending the value only
        // ever echoes what the row held at page load.
        //
        // The backend gate (`validate_csm_view_mode`, accounts/serializers/
        // team_settings.py) rejects a non-admin CHANGING this field — not writing it.
        // An echo normally equals the stored value, so it passed. The reachable failure
        // is the concurrent one: an admin flips the mode after this page loaded, the
        // echo is now a change, and the non-admin's unrelated save (currency, say) 400s
        // with a message about a control they cannot see. It clears on reload, not by
        // retrying, because the stale value sits in React state.
        //
        // ⚠️ This is the narrow half of a wider issue that is NOT fixed here. Every
        // field below is echoed from page-load state when the user did not edit it, so
        // two people saving from pages loaded at different times revert each other on
        // `currency` too. Closing that needs the form to track which controls were
        // actually edited and send only those. Filed, not silently absorbed: see the PR
        // body.
        ...(team?.is_admin &&
          csmViewMode !== null && { csm_view_mode: csmViewMode }),
      };

      // Save to backend
      await apiClient.patch(`/team-settings/${teamId}/`, payload);

      showToast({
        content: "Settings saved successfully!",
        type: NOTIFICATION_TYPES.success,
      });

      // Dispatch custom event to notify other components about currency change
      window.dispatchEvent(
        new CustomEvent("currencyChanged", {
          detail: { currency: defaultCurrency },
        }),
      );
      window.dispatchEvent(new CustomEvent("teamSettingsChanged"));
    } catch (error) {
      console.error("Error saving team settings:", error);
      showToast({
        content: "Failed to save settings. Please try again.",
        type: NOTIFICATION_TYPES.error,
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="rounded-lg bg-white p-6 shadow-sm">
        <div className="animate-pulse">
          <div className="mb-6 h-8 w-1/4 rounded bg-gray-200"></div>
          <div className="mb-4 h-20 rounded bg-gray-200"></div>
          <div className="h-10 w-32 rounded bg-gray-200"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg bg-white p-6 shadow-sm">
      <h2 className="mb-6 text-2xl font-semibold text-gray-900">
        Team Settings
      </h2>

      <div className="space-y-6">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div>
            <label
              htmlFor="defaultCurrency"
              className="mb-2 block text-sm font-medium text-gray-700"
            >
              Default Currency
            </label>
            <Select
              id="defaultCurrency"
              value={defaultCurrency}
              onChange={(e) => setDefaultCurrency(e.target.value)}
              disabled={isSaving}
            >
              {currencies.map((currency) => (
                <option key={currency.value} value={currency.value}>
                  {currency.label}
                </option>
              ))}
            </Select>
          </div>
        </div>

        {team?.is_admin && (
          <div className="border-t border-gray-200 pt-6">
            <h3 className="mb-1 text-lg font-semibold text-gray-900">
              CSM View Mode
            </h3>
            <p className="mb-4 text-sm text-gray-500">
              Control whether CSMs can switch between viewing all accounts or
              only their own book of business.
            </p>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div>
                <label
                  htmlFor="csmViewMode"
                  className="mb-2 block text-sm font-medium text-gray-700"
                >
                  View mode
                </label>
                <Select
                  id="csmViewMode"
                  value={csmViewMode || "open"}
                  onChange={(e) => setCsmViewMode(e.target.value)}
                  disabled={isSaving}
                >
                  <option value="open">
                    Open — CSMs can switch between views
                  </option>
                  <option value="restricted">
                    Restricted — CSMs see only their book
                  </option>
                </Select>
              </div>
            </div>
          </div>
        )}

        <div className="border-t border-gray-200 pt-4">
          <Button
            onClick={handleSave}
            disabled={isSaving || !teamSettings}
            isProcessing={isSaving}
            color="blue"
          >
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </div>

        <div className="border-t border-gray-200 pt-6">
          <h3 className="mb-2 text-lg font-semibold text-gray-900">
            Team Goals
          </h3>
          <p className="mb-4 text-sm text-gray-500">
            Set quarterly retention, expansion, and pilot conversion targets for
            your team.
          </p>
          <Button color="light" onClick={handleOpenGoalsModal}>
            Configure Team Goals
          </Button>
          <TeamGoalsModal
            open={goalsModalOpen}
            onClose={() => setGoalsModalOpen(false)}
            revenueUnit={
              teamSettings?.dashboard_revenue_unit?.toUpperCase() === "ARR"
                ? "ARR"
                : "MRR"
            }
          />
        </div>
      </div>
    </div>
  );
};
